import { KPIPicker } from "@/components/kpi/KPIPicker";
import { useUnsentChatGuard } from "@/components/chat/UnsentChatGuard";
import { useState, useEffect, useRef } from "react";
import { visibleInterval } from "@/lib/poll";
import { ParentChip } from "@/lib/parents";
import { TemplatePicker, SaveTemplateDialog, applyTemplate } from "@/components/shared/TaskTemplates";
import { DatePicker } from "@/components/form/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextField, TextArea, SelectField, DateField } from "@/components/form/Fields";
import { CompletedOn } from "@/components/modals/CompletedOn";
import { ProcedurePicker } from "@/components/procedures/ProcedurePicker";
import { MentionTextarea } from "@/components/form/MentionTextarea";
import { MultiSelect } from "@/components/form/MultiSelect";
import { AssigneePicker } from "@/components/form/AssigneePicker";
import { useOptions, nonPropertyOrgOptions, propertyOptions, contactOptions, userOptions, quickAddOrg, quickAddContact } from "@/components/hooks/useOptions";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/components/hooks/useConfig";
import { useActionCategories } from "@/components/hooks/useActionCategories";
import { usePermissions } from "@/context/PermissionsContext";
import { ActionAttachments, uploadPending } from "@/components/shared/ActionAttachments";
import { ChatConversation } from "@/components/chat/ChatConversation";
import { RecurringScopeDialog } from "@/components/shared/RecurringScopeDialog";
import client, { formatApiErrorDetail } from "@/lib/api";
import { saveAction } from "@/lib/saveAction";
import { toast } from "sonner";
import { PRIORITIES, REMINDERS, RECURRING, ACTIVITY_TYPES, statusLabel } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { sortSubtasks } from "@/lib/taskSort";
import { ListChecks, UserPlus, Plus, X, Check, GripVertical, MessageCircle, UserCircle2, History, Eye, Bell, BellOff, AlarmClock } from "lucide-react";
import { TaskHistoryTab } from "@/components/shared/TaskHistoryTab";
import { PropertySubtaskTools } from "@/components/shared/PropertySubtaskTools";
import { useTickComment, withTickComment } from "@/lib/tickComment";
import { FollowUpSection } from "@/components/shared/FollowUpSection";
import { CreatedByNote } from "@/components/shared/CreatedByNote";
import { same } from "@/lib/saveAction";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ModalNotes } from "@/components/shared/ModalNotes";

const REPEAT_OPTIONS = RECURRING.filter((r) => r !== "None");

export function TaskChatTab({ taskId }) {
  const [chatId, setChatId] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    client.post(`/chats/from-task/${taskId}`).then(({ data }) => { if (alive) setChatId(data.id); }).catch(() => setErr(true));
    return () => { alive = false; };
  }, [taskId]);
  if (err) return <div className="py-10 text-center text-sm text-slate-400">Could not load task chat</div>;
  if (!chatId) return <div className="py-10 text-center text-sm text-slate-400">Loading chat…</div>;
  return (
    <div className="h-[60vh] w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-slate-200" data-testid="task-chat-tab">
      <ChatConversation chatId={chatId} embedded />
    </div>
  );
}

function fmtDoneAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function SubtaskDue({ value, onChange, done, i }) {
  const ref = useRef(null);
  const open = () => { try { ref.current?.showPicker(); } catch { ref.current?.focus(); } };
  const today = new Date().toLocaleDateString("en-CA");
  const tone = done ? "text-slate-400" : value < today ? "text-red-600" : value === today ? "text-amber-600" : "text-sky-700";
  return (
    <span className="relative flex shrink-0 items-center">
      <input ref={ref} type="date" value={value || ""} onChange={(e) => onChange(e.target.value || null)} tabIndex={-1}
        className="pointer-events-none absolute bottom-0 left-0 h-0 w-0 opacity-0" data-testid={`subtask-due-input-${i}`} />
      {value ? (
        <span className={`flex items-center gap-0.5 rounded-md bg-slate-50 px-1.5 py-0.5 text-[11px] font-semibold ${tone}`} data-testid={`subtask-due-${i}`}>
          <button type="button" onClick={open} className="flex items-center gap-1" title="Change due date"><AlarmClock className="h-3.5 w-3.5" />
            {new Date(`${value}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</button>
          <button type="button" onClick={() => onChange(null)} title="Clear due date" className="ml-0.5 text-slate-400 hover:text-red-600" data-testid={`subtask-due-clear-${i}`}><X className="h-3 w-3" /></button>
        </span>
      ) : (
        <button type="button" onClick={open} title="Add due date" className="rounded p-0.5 text-slate-300 hover:bg-sky-50 hover:text-sky-600" data-testid={`subtask-due-add-${i}`}><AlarmClock className="h-4 w-4" /></button>
      )}
    </span>
  );
}

function SortableSubtask({ id, s, i, users, patch, toggle, remove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const [draft, setDraft] = useState(s.title || "");
  useEffect(() => { setDraft(s.title || ""); }, [s.title]);
  const commit = () => { if (draft !== (s.title || "")) patch(id, "title", draft); };
  return (
    <div ref={setNodeRef} style={style} data-testid={`subtask-row-${i}`}>
     <div className="flex items-start gap-1.5">
      <button type="button" {...attributes} {...listeners} data-testid={`subtask-drag-${i}`}
        className="mt-1 cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      <button type="button" onClick={() => toggle(id)} data-testid={`subtask-toggle-${i}`}
        className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${s.completed ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-emerald-400"}`}>
        <Check className="h-3 w-3" />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
            data-testid={`subtask-title-${i}`}
            className={`min-w-0 flex-1 rounded-md border border-slate-200 px-2 py-1 text-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300 ${s.completed ? "text-slate-400 line-through" : "text-slate-700"}`} />
          <AssigneePicker value={s.assigned_to_id} onChange={(v) => patch(id, "assigned_to_id", v)} users={users} multi={false} floatRight size="h-6 w-6" placeholder="Assign" testid={`subtask-assignee-${i}`} />
          <SubtaskDue value={s.due_date} onChange={(v) => patch(id, "due_date", v)} done={s.completed} i={i} />
        </div>
        {s.completed && (s.completed_at || s.completed_by_name) && (
          <span className="mt-1 block truncate text-[11px] text-emerald-600" data-testid={`subtask-done-meta-${i}`}>
            ✓ {s.completed_by_name || "Done"}{s.completed_at ? ` · ${fmtDoneAt(s.completed_at)}` : ""}
          </span>
        )}
      </div>
      <button type="button" onClick={() => remove(id)} data-testid={`subtask-remove-${i}`}
        className="mt-1 rounded p-0.5 text-slate-300 hover:bg-red-50 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
     </div>
    </div>
  );
}

export function SubtaskEditor({ subtasks, users, currentUserId, onChange, hideHeader = false, properties }) {
  const [text, setText] = useState("");
  const list = subtasks || [];
  const done = list.filter((s) => s.completed).length;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const keyOf = (s, i) => s.id || s._uid || `i${i}`;
  // Rows keep their place while the task is open; completed items sort to the bottom on next open.
  const [order, setOrder] = useState(() => sortSubtasks(list).map((s, i) => keyOf(s, i)));
  const listKeys = list.map((s, i) => keyOf(s, i));
  const membership = [...listKeys].sort().join("|");
  useEffect(() => {
    setOrder((o) => [...o.filter((k) => listKeys.includes(k)), ...listKeys.filter((k) => !o.includes(k))]);
  }, [membership]); // eslint-disable-line react-hooks/exhaustive-deps
  const byKey = Object.fromEntries(list.map((s, i) => [keyOf(s, i), s]));
  const ids = [...order.filter((k) => byKey[k]), ...listKeys.filter((k) => !order.includes(k))];
  const sorted = ids.map((k) => byKey[k]);

  const add = () => {
    const t = text.trim();
    if (!t) return;
    const newId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `u${Date.now()}${Math.random()}`;
    onChange([...list, { id: newId, title: t, assigned_to_id: null, completed: false }]);
    setText("");
  };
  const matchKey = (s, i, k) => keyOf(s, i) === k;
  const patch = (k, key, val) => onChange(list.map((s, idx) => (matchKey(s, idx, k) ? { ...s, [key]: val } : s)));
  const toggle = (k) => onChange(list.map((s, idx) => {
    if (!matchKey(s, idx, k)) return s;
    const nowDone = !s.completed;
    return { ...s, completed: nowDone,
      completed_by_id: nowDone ? currentUserId : null,
      completed_by_name: nowDone ? (users.find((u) => u.id === currentUserId)?.name) : null,
      completed_at: nowDone ? new Date().toISOString() : null };
  }));
  const remove = (k) => onChange(list.filter((s, idx) => !matchKey(s, idx, k)));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = ids.indexOf(active.id);
    const newI = ids.indexOf(over.id);
    if (oldI < 0 || newI < 0) return;
    const moved = arrayMove(sorted, oldI, newI);
    setOrder(moved.map((s, i) => keyOf(s, i)));
    onChange(moved);
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="subtask-editor">
      {!hideHeader && <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
          <ListChecks className="h-3.5 w-3.5" /> Sub-tasks
        </p>
        {list.length > 0 && <span className="text-xs font-semibold text-slate-500" data-testid="subtask-progress">{done}/{list.length} done</span>}
      </div>}

      <div className="flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Add a sub-task…" data-testid="subtask-new-input"
          className="flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300" />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="subtask-add-button"><Plus className="h-4 w-4" /></Button>
      </div>
      {properties && <PropertySubtaskTools properties={properties} list={list} onChange={onChange} />}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sorted.map((s, i) => <SortableSubtask key={ids[i]} id={ids[i]} s={s} i={i} users={users} patch={patch} toggle={toggle} remove={remove} />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function MuteToggle({ record, userId }) {
  const [muted, setMuted] = useState((record?.muted_by || []).includes(userId));
  useEffect(() => { setMuted((record?.muted_by || []).includes(userId)); }, [record?.id, record?.muted_by, userId]);
  const toggle = async () => {
    const next = !muted;
    setMuted(next);
    try {
      await client.post(`/actions/${record.id}/mute`, { muted: next });
      toast.success(next ? "Notifications muted for this task (chat still alerts)" : "Notifications turned back on");
    } catch { setMuted(!next); toast.error("Could not update"); }
  };
  const Icon = muted ? BellOff : Bell;
  return (
    <button type="button" onClick={toggle} data-testid="task-mute-toggle" title={muted ? "Unmute notifications for this task" : "Mute notifications for this task"}
      className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold transition-colors ${muted ? "bg-amber-50 text-amber-700 hover:bg-amber-100" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}>
      <Icon className="h-3.5 w-3.5" />{muted ? "Muted" : "Mute"}
    </button>
  );
}

export function TaskModal({ open, record, prefill = {}, onClose, onSuccess }) {
  const [guard, unsentDialog] = useUnsentChatGuard();
  const { user, users } = useAuth();
  const { orgs, contacts, props } = useOptions(open);
  const cfg = useConfig();
  const { subtypes } = useActionCategories();
  const { showSubtypes, can } = usePermissions();
  const isEdit = record && record.id;

  const toForm = (base) => {
    const orgIds = base.organisation_ids?.length ? base.organisation_ids : (base.organisation_id ? [base.organisation_id] : []);
    const propIds = base.property_ids?.length ? base.property_ids : (base.property_id ? [base.property_id] : []);
    const assignedIds = base.assigned_to_ids?.length ? base.assigned_to_ids : (base.assigned_to_id ? [base.assigned_to_id] : (user?.id ? [user.id] : []));
    const subTypes = base.sub_types?.length ? base.sub_types : (base.sub_type ? [base.sub_type] : []);
    return { ...base, sub_types: subTypes, organisation_ids: orgIds, property_ids: propIds, assigned_to_ids: assignedIds, subtasks: base.subtasks || [] };
  };
  const [form, setForm] = useState(() => toForm(record || { status: "To Do", priority: "Medium", assigned_to_ids: user?.id ? [user.id] : [], ...prefill }));
  const savedRef = useRef(record);
  const [savedRec, setSavedRec] = useState(record);
  const [savingTpl, setSavingTpl] = useState(false);
  const followRef = useRef();
  // Saves run one at a time so each is based on the previous result (rapid ticks can't overwrite each other)
  const saveQueue = useRef(Promise.resolve());
  const pending = useRef(0);
  const persist = (payload, scope) => {
    pending.current += 1;
    const run = saveQueue.current.then(async () => {
      // Completion fields are server-managed; a stale form copy must not overwrite them
      const { completed_date, completed_by_id, completed_at, ...clean } = payload; // eslint-disable-line no-unused-vars
      const data = await saveAction(savedRef.current, clean, scope);
      savedRef.current = data;
      if (data?.conflicts?.length) setForm(toForm(data));
      return data;
    }).finally(() => { pending.current -= 1; });
    saveQueue.current = run.catch(() => {});
    return run;
  };
  const [viewers, setViewers] = useState([]);
  // Presence heartbeat + pull in teammates' changes without touching fields being edited
  useEffect(() => {
    if (!open || !isEdit) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await client.post(`/actions/${record.id}/presence`);
        if (!alive) return;
        setViewers(data.viewers || []);
        if (pending.current) return;
        if (data.updated_at && data.updated_at !== savedRef.current?.updated_at) {
          const before = savedRef.current;
          const { data: fresh } = await client.get(`/actions/${record.id}`);
          // Skip if one of our own saves started or finished meanwhile – this copy may be older
          if (!alive || pending.current || savedRef.current !== before || (fresh.updated_at || "") < (before?.updated_at || "")) return;
          const prev = toForm(savedRef.current || {});
          const next = toForm(fresh);
          savedRef.current = fresh;
          setForm((f) => {
            const merged = { ...f };
            Object.keys(next).forEach((k) => { if (same(f[k], prev[k])) merged[k] = next[k]; });
            return merged;
          });
        }
      } catch (e) { /* ignore transient errors */ }
    };
    tick();
    const stop = visibleInterval(tick, 5000);
    return () => { alive = false; stop(); client.delete(`/actions/${record.id}/presence`).catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, record?.id]);
  const [repeat, setRepeat] = useState(() => Boolean(record?.recurring && record.recurring !== "None"));
  const isSeriesEdit = Boolean(isEdit && repeat);
  const tick = useTickComment();
  const endType = form.recur_end_type || "never";
  const [scopeOpen, setScopeOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState("save");
  const [isRange, setIsRange] = useState(() => Boolean((record || prefill)?.due_start));
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tab, setTab] = useState("details");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const [savedTick, setSavedTick] = useState(false);
  const autoSave = async (override) => {
    if (!isEdit || isSeriesEdit) return;
    const next = { ...form, ...(override || {}) };
    if (!next.title?.trim()) return;
    if (isRange && next.due_start && next.due_date && next.due_date < next.due_start) return;
    try {
      const payload = { ...next, action_type: "Task", assigned_to_id: null, property_id: null, recurring: next.recurring || null };
      const data = await persist(payload);
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1600);
      if (data && data.status && data.status !== next.status) setForm((f) => ({ ...f, status: data.status }));
      if (data) setSavedRec(data);
      onSuccess && onSuccess(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const setAuto = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); autoSave({ [k]: v }); };
  // Recurring tasks: changing the status saves this occurrence straight away, like ticking a sub-task
  const setStatus = (v) => {
    if (!isSeriesEdit) return setAuto("status")(v);
    setForm((f) => ({ ...f, status: v }));
    persist({ ...savedRef.current, status: v }).then((data) => {
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1600);
      if (data) setSavedRec(data);
      onSuccess && onSuccess(data);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  // Recurring tasks: ticking a sub-task saves this occurrence only; other edits wait for Save + scope.
  const onSubtasksChange = (v) => {
    if (!isSeriesEdit) return setAuto("subtasks")(v);
    const old = Object.fromEntries((form.subtasks || []).map((s) => [s.id, s]));
    const toggleOnly = v.length === Object.keys(old).length && v.some((s) => old[s.id] && !!old[s.id].completed !== !!s.completed)
      && v.every((s) => s.id && old[s.id] && old[s.id].title === s.title && (old[s.id].assigned_to_id || null) === (s.assigned_to_id || null));
    setForm((f) => ({ ...f, subtasks: v }));
    if (!toggleOnly) return;
    const byId = Object.fromEntries(v.map((s) => [s.id, s]));
    const saved = savedRef.current;
    const subs = (saved.subtasks || []).map((s) => (s.id in byId ? { ...s, completed: byId[s.id].completed, ...(byId[s.id].comment !== undefined ? { comment: byId[s.id].comment } : {}) } : s));
    persist({ ...saved, subtasks: subs }).then((data) => {
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1600);
      if (data?.status) setForm((f) => ({ ...f, status: data.status }));
      onSuccess && onSuccess(data);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  const setReminder = (v) => {
    const patch = { reminder: v };
    if (v !== "Custom days before") patch.reminder_days = null;
    if (v !== "On a specific date") patch.reminder_date = null;
    setForm((f) => ({ ...f, ...patch }));
    autoSave(patch);
  };

  const toggleRange = () => {
    setIsRange((r) => {
      const next = !r;
      const ds = next ? (form.due_start || form.due_date || new Date().toLocaleDateString("en-CA")) : null;
      setForm((f) => ({ ...f, due_start: ds }));
      autoSave({ due_start: ds });
      return next;
    });
  };

  const [stopOpen, setStopOpen] = useState(false);
  const untickRepeat = async () => {
    setRepeat(false);
    setForm((f) => ({ ...f, recurring: null }));
    try {
      const data = await persist({ ...form, action_type: "Task", assigned_to_id: null, property_id: null, recurring: null });
      onSuccess && onSuccess(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const pickStop = async (k) => {
    setStopOpen(false);
    if (k === "delete") {
      try { const { data } = await client.delete(`/actions/${record.id}/series-future`); toast.success(`Removed ${data.removed} future task${data.removed === 1 ? "" : "s"}`); }
      catch (e) { return toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    }
    await untickRepeat();
  };
  const toggleRepeat = (checked) => {
    if (!checked && isEdit && record?.series_id) { setStopOpen(true); return; }
    setRepeat(checked);
    const rec = checked ? (form.recurring && form.recurring !== "None" ? form.recurring : "Weekly") : null;
    setForm((f) => ({ ...f, recurring: rec }));
    autoSave({ recurring: rec });
  };

  const assignedElsewhere = isEdit && record.created_by_name && record.created_by_id !== record.assigned_to_id && !(record.assigned_to_ids || []).includes(record.created_by_id);

  const rangeError = isRange && form.due_start && form.due_date && form.due_date < form.due_start;

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title is required");
    if (rangeError) return toast.error("End date can't be before the start date");
    if (isSeriesEdit) { setPendingAction("save"); setScopeOpen(true); return; }
    setSaving(true);
    try {
      const payload = { ...form, action_type: "Task", assigned_to_id: null, property_id: null, recurring: repeat ? (form.recurring || "Weekly") : null };
      if (isEdit) delete payload.notes;
      const data = isEdit ? await persist(payload) : (await client.post("/actions", payload)).data;
      if (!isEdit && pendingFiles.length) { await uploadPending("action", data.id, pendingFiles); setPendingFiles([]); }
      await followRef.current?.submitIfPending();
      toast.success(isEdit ? "Task updated" : "Task created");
      onSuccess && onSuccess(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyScope = async (scope) => {
    setSaving(true);
    try {
      if (pendingAction === "delete") {
        await client.delete(`/actions/${record.id}?scope=${scope}`);
        toast.success("Task deleted");
      } else {
        const payload = { ...form, action_type: "Task", assigned_to_id: null, property_id: null, recurring: repeat ? (form.recurring || "Weekly") : null };
        delete payload.notes;
        const data = await persist(payload, scope);
        await followRef.current?.submitIfPending();
        toast.success("Task updated");
        onSuccess && onSuccess(data);
        setScopeOpen(false);
        onClose();
        return;
      }
      onSuccess && onSuccess();
      setScopeOpen(false);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && guard(onClose)()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto overflow-x-hidden sm:max-w-lg [&>*]:min-w-0" data-testid="task-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ListChecks className="h-5 w-5 text-sky-600" /> {isEdit ? "Edit Task" : "New Task"}
            {isEdit && savedTick && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600" data-testid="task-autosaved"><Check className="h-3 w-3" /> Saved</span>}
            {isEdit && <MuteToggle record={record} userId={user?.id} />}
            {isEdit && tab === "details" && (
              <Button onClick={save} disabled={saving} data-testid="task-save-button-top" size="sm" className="mr-6 bg-sky-600 hover:bg-sky-700">
                {saving ? "Saving…" : "Save Task"}
              </Button>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">Create or edit a task</DialogDescription>
          {form.parent_type && <div><ParentChip task={{ ...form, id: record?.id }} onNavigate={onClose} /></div>}
        </DialogHeader>

        {isEdit && (
          <div className="flex items-center gap-1 border-b border-slate-100 pb-2">
            {[["details", "Details"], ["chat", "Chat"], ["history", "History"]].map(([val, label]) => (
              <button key={val} onClick={() => setTab(val)} data-testid={`task-tab-${val}`}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${tab === val ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-slate-50"}`}>
                {val === "chat" && <MessageCircle className="h-4 w-4" />}{val === "history" && <History className="h-4 w-4" />}{label}
              </button>
            ))}
          </div>
        )}

        {isEdit && viewers.length > 0 && (
          <p className="flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-700" data-testid="task-viewers-note">
            <Eye className="h-3.5 w-3.5" />
            {viewers.map((v) => v.name?.split(" ")[0] || "Someone").join(", ")} {viewers.length > 1 ? "are" : "is"} also viewing this task
          </p>
        )}

        {isEdit && tab === "history" ? (
          <div className="py-2"><TaskHistoryTab key={record.updated_at} taskId={record.id} /></div>
        ) : isEdit && tab === "chat" ? (
          <div className="min-w-0 py-2"><TaskChatTab taskId={record.id} /></div>
        ) : (
        <div className="space-y-4 py-2">
          {!isEdit && <TemplatePicker onPick={(t) => { setForm((f) => applyTemplate(t, f)); toast.success(`Template "${t.name}" applied`); }} />}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2" onBlur={() => autoSave()}>
              <TextField label="Title" required value={form.title} onChange={set("title")} testid="task-field-title" placeholder="e.g. Prepare Q3 campaign brief" />
            </div>
            <SelectField label="Type" value={form.activity_type || "Task"} onChange={setAuto("activity_type")} options={ACTIVITY_TYPES} allowEmpty={false} testid="task-field-type" />
          </div>

          {assignedElsewhere && (
            <p className="flex items-center gap-1.5 rounded-md bg-sky-50 px-3 py-1.5 text-xs text-sky-700" data-testid="task-assignor-note">
              <UserPlus className="h-3.5 w-3.5" /> Assigned by {record.created_by_name}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Assigned To</label>
              <AssigneePicker multi value={form.assigned_to_ids || []} onChange={setAuto("assigned_to_ids")} users={users} testid="task-field-assignee" placeholder="Assign users" />
            </div>
            <SelectField label="Priority" value={form.priority} onChange={setAuto("priority")} options={PRIORITIES} allowEmpty={false} testid="task-field-priority" />
          </div>
          {can("kpis", "view") && (
            <KPIPicker value={form.kpi_ids || []} onChange={setAuto("kpi_ids")} userIds={form.assigned_to_ids || []} testid="task-field-kpis" />
          )}

          {isEdit && savedRec?.status === "Completed" && <CompletedOn key={savedRec.completed_date || ""} record={savedRec} onSaved={(d) => { savedRef.current = { ...savedRef.current, ...d }; onSuccess && onSuccess(d); }} />}
          {isEdit && savedRec?.status !== "Completed" && form.status === "Completed" && (
            <p className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 text-[11px] text-slate-600" data-testid="task-completed-on-hint">Save the task as Completed, then reopen it to change the completed date for KPIs.</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Status" value={form.status} onChange={setStatus} options={(cfg.action_statuses || []).map((s) => ({ value: s, label: statusLabel(s) }))} allowEmpty={false} testid="task-field-status" />
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-xs font-semibold text-slate-600">{isRange ? "Date Range" : "Due Date"}</label>
                <button type="button" onClick={toggleRange} data-testid="task-range-toggle" className="text-[11px] font-semibold text-sky-600 hover:underline">
                  {isRange ? "Single date" : "Date range"}
                </button>
              </div>
              {isRange ? (
                <div className="grid grid-cols-2 gap-2">
                  <DateField value={form.due_start} onChange={setAuto("due_start")} testid="task-field-due-start" />
                  <DateField value={form.due_date} onChange={setAuto("due_date")} testid="task-field-due-end" monthHint={form.due_start} />
                </div>
              ) : (
                <DateField value={form.due_date} onChange={setAuto("due_date")} testid="task-field-due" />
              )}
              {rangeError && <p className="mt-1 text-[11px] font-medium text-rose-600" data-testid="task-range-error">End date can't be before the start date.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <SelectField label="Reminder" value={form.reminder || "None"} onChange={setReminder} options={REMINDERS} allowEmpty={false} testid="task-field-reminder" />
              {form.reminder === "Custom days before" && (
                <input type="number" min="1" value={form.reminder_days ?? ""} data-testid="task-field-reminder-days"
                  onChange={(e) => setAuto("reminder_days")(e.target.value ? Number(e.target.value) : null)}
                  placeholder="Days before due date"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100" />
              )}
              {form.reminder === "On a specific date" && (
                <DateField value={form.reminder_date} onChange={setAuto("reminder_date")} testid="task-field-reminder-date" />
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-600">Repeat</label>
              <label className="flex h-[38px] items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm text-slate-700">
                <input type="checkbox" checked={repeat} onChange={(e) => toggleRepeat(e.target.checked)} data-testid="task-field-repeat" className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400" />
                Repeats
              </label>
            </div>
          </div>
          {repeat && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <SelectField label="Repeat Frequency" value={form.recurring} onChange={setAuto("recurring")} options={REPEAT_OPTIONS} allowEmpty={false} testid="task-field-frequency" />
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600">Ends</label>
                <select
                  value={endType}
                  onChange={(e) => setAuto("recur_end_type")(e.target.value)}
                  data-testid="task-field-recur-endtype"
                  className="h-[38px] w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-sky-400 focus:outline-none"
                >
                  <option value="never">Never</option>
                  <option value="date">On Date</option>
                  <option value="count">After X Times</option>
                </select>
              </div>
              {endType === "date" && (
                <div>
                  <label className="invisible mb-1.5 block text-xs font-semibold">Ends</label>
                  <DatePicker label="Ends" value={form.recur_end_date} onChange={setAuto("recur_end_date")} testid="task-field-recur-enddate" className="h-[38px]" />
                </div>
              )}
              {endType === "count" && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Occurrences</label>
                  <input
                    type="number" min="1"
                    value={form.recur_count || ""}
                    onChange={(e) => setAuto("recur_count")(e.target.value ? parseInt(e.target.value, 10) : null)}
                    data-testid="task-field-recur-count" placeholder="e.g. 10"
                    className="h-[38px] w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-sky-400 focus:outline-none"
                  />
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg bg-slate-50 p-3 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Links (optional)</p>
            <div>
              <MultiSelect propertyField label="Property" value={form.property_ids || []} onChange={setAuto("property_ids")} options={propertyOptions(props)} testid="task-field-property" placeholder="Link properties…" />
            </div>
            <MultiSelect label="Organisations" value={form.organisation_ids || []} onChange={setAuto("organisation_ids")} options={nonPropertyOrgOptions(orgs)} onQuickAdd={quickAddOrg} testid="task-field-org" placeholder="Link organisations…" />
            <MultiSelect label="Contacts" value={form.contact_ids || []} onChange={setAuto("contact_ids")} options={contactOptions(contacts)} onQuickAdd={quickAddContact} testid="task-field-contacts" placeholder="Link contacts…" />
          </div>

          <FollowUpSection ref={followRef} parent={form} orgs={orgs} contacts={contacts} users={users} currentUserId={user?.id} />

          <ProcedurePicker value={form.procedure_id} onChange={setAuto("procedure_id")} />

          <SubtaskEditor subtasks={form.subtasks} users={users} currentUserId={user?.id} onChange={async (v) => { const nv = await withTickComment(record, form.subtasks, v, tick.ask); if (nv) onSubtasksChange(nv); }} properties={(props || []).filter((o) => o.org_type === "DTR Property").map((o) => ({ id: o.id, name: o.name }))} />
          {tick.dialog}

          <div onBlur={() => autoSave()}>
            <ModalNotes entityType="action" collection="actions" record={record} value={form.notes} onChange={set("notes")} testid="task-field-notes" />
            <ActionAttachments recordId={isEdit ? record.id : null} files={pendingFiles} setFiles={setPendingFiles} />
          </div>
        </div>
        )}

        {isEdit && <div className="flex justify-start"><CreatedByNote record={record} /></div>}
        {unsentDialog}
        <DialogFooter>
          {!(isEdit && tab !== "details") && (
            <Button variant="outline" onClick={() => (form.title?.trim() ? setSavingTpl(true) : toast.error("Add a task name first"))} data-testid="task-save-template-button" className="sm:mr-auto">Save as template</Button>
          )}
          <Button variant="ghost" onClick={guard(onClose)}>Cancel</Button>
          {isEdit && isSeriesEdit && (
            <Button variant="outline" onClick={() => { setPendingAction("delete"); setScopeOpen(true); }} data-testid="task-delete-series-button" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">Delete</Button>
          )}
          {!(isEdit && tab !== "details") && (
            <Button onClick={guard(save)} disabled={saving} data-testid="task-save-button" className="bg-sky-600 hover:bg-sky-700">
              {saving ? "Saving…" : isEdit ? "Save Task" : "Create Task"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {savingTpl && <SaveTemplateDialog form={form} onClose={() => setSavingTpl(false)} />}
      <RecurringScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        busy={saving}
        danger={pendingAction === "delete"}
        title={pendingAction === "delete" ? "Delete recurring task" : "Save recurring task"}
        description={pendingAction === "delete" ? "This task repeats. Choose which tasks to delete:" : "This task repeats. Apply changes to:"}
        onPick={applyScope}
      />
      <RecurringScopeDialog
        open={stopOpen}
        onOpenChange={setStopOpen}
        title="Stop repeating"
        description="This task won't repeat any more. What should happen to the future tasks in this series that aren't done yet?"
        options={[["keep", "Keep future tasks"], ["delete", "Delete future tasks"]]}
        onPick={pickStop}
      />
    </Dialog>
  );
}
